﻿import React from 'react'
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { Button, Heading, Text, LoadingSpinner, Tooltip as UiTooltip } from '../../components/ui'
import { SortableTableHeader } from '../../components/ui/SortableTableHeader'
import { PermissionGuard } from '../../components/PermissionGuard'
import { modulesService } from '../../services/modules'
import { admDashboardService } from '../../services/admDashboard'
import type { AdmNfeHeader, AdmNfeItem, AdmNfseHeader, AdmNfseItem, AdmWithoutNfRow } from '../../types/admDespesas'
import type { AdmDashboardFilters, AdmGroupRow } from '../../types/admDashboard'
import {
  CurrencyDollarIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  XMarkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('pt-BR')
}



function withinPeriod(dateStr: string | null, from: string, to: string): boolean {
  if (!dateStr) return true  // sem data: inclui sempre
  if (!from && !to) return true
  const d = dateStr.slice(0, 10)
  if (from && d < from) return false
  if (to && d > to) return false
  return true
}

// Paleta de cores para gráficos
const CHART_COLORS = [
  '#008B95', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
]

type ActiveTab = 'nfe' | 'nfse' | 'sem_nf' | 'suppliers'

const InfoTip: React.FC<{ content: React.ReactNode; position?: 'top' | 'bottom' | 'left' | 'right' }> = ({ content, position = 'bottom' }) => (
  <UiTooltip content={content} position={position} constrainWidth className="inline-flex shrink-0 align-middle">
    <span
      className="inline-flex rounded-full p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
      aria-label="Saiba mais"
    >
      <InformationCircleIcon className="h-4 w-4" />
    </span>
  </UiTooltip>
)

// ── Componente KPI Card ───────────────────────────────────────────────────────

interface KpiCardProps {
  label: string
  value: string
  icon: React.ReactNode
  color: string
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 flex items-center gap-4">
    <div className={`flex-shrink-0 h-12 w-12 rounded-lg flex items-center justify-center ${color}`}>
      {icon}
    </div>
    <div className="min-w-0">
      <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">{label}</Text>
      <p className="text-xl font-bold text-gray-900 dark:text-white truncate">{value}</p>
    </div>
  </div>
)

// ── Componente Tabela de Ranking ──────────────────────────────────────────────

interface RankingTableProps {
  rows: AdmGroupRow[]
  emptyLabel: string
}

const RankingTable: React.FC<RankingTableProps> = ({ rows, emptyLabel }) => {
  if (rows.length === 0) {
    return <Text size="sm" color="muted">{emptyLabel}</Text>
  }
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto overflow-y-auto max-h-[400px]">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">#</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Nome</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Qtd</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Total</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">%</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map((row, idx) => (
              <tr key={row.label} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-2 text-gray-400 dark:text-gray-500 font-mono text-xs">{idx + 1}</td>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-medium">{row.label}</td>
                <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{row.count}</td>
                <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100 font-medium">{formatCurrency(row.total)}</td>
                <td className="px-3 py-2 text-right">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-gray-500 dark:text-gray-400">{row.share.toFixed(1)}%</span>
                    <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full bg-primary-500"
                        style={{ width: `${Math.min(row.share, 100)}%` }}
                      />
                    </div>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Modal NF-e ────────────────────────────────────────────────────────────────

interface NfeDetailModalProps {
  nfe: AdmNfeHeader
  items: AdmNfeItem[]
  categoryMap: Map<string, string>
  costCenterMap: Map<string, string>
  onClose: () => void
}

const NfeDetailModal: React.FC<NfeDetailModalProps> = ({ nfe, items, categoryMap, costCenterMap, onClose }) => {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
              NF-e {nfe.number ? `nº ${nfe.number}` : '—'}
              {nfe.series ? ` · Série ${nfe.series}` : ''}
            </Heading>
            <Text size="xs" color="muted" className="mt-0.5 font-mono">{nfe.access_key}</Text>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-6">

          {/* Dados gerais */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Emissão</Text>
              <p className="text-gray-900 dark:text-gray-100">{formatDate(nfe.issue_date)}</p>
            </div>
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Forma de Pagamento</Text>
              <p className="text-gray-900 dark:text-gray-100">{nfe.payment_method || '—'}</p>
            </div>
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Emitente</Text>
              <p className="text-gray-900 dark:text-gray-100 font-medium">{nfe.issuer_name || '—'}</p>
              {nfe.issuer_cnpj && <Text size="xs" color="muted">{nfe.issuer_cnpj}</Text>}
            </div>
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Destinatário</Text>
              <p className="text-gray-900 dark:text-gray-100">{nfe.recipient_name || '—'}</p>
              {nfe.recipient_document && <Text size="xs" color="muted">{nfe.recipient_document}</Text>}
            </div>
          </div>

          {/* Valores */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Produtos', value: nfe.total_products },
              { label: 'Frete', value: nfe.total_freight },
              { label: 'Desconto', value: nfe.total_discount },
              { label: 'Total NF-e', value: nfe.total_value, highlight: true },
            ].map(({ label, value, highlight }) => (
              <div key={label} className={`rounded-lg p-3 ${highlight ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">{label}</Text>
                <p className={`text-base font-bold mt-0.5 ${highlight ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-gray-100'}`}>
                  {formatCurrency(value ?? 0)}
                </p>
              </div>
            ))}
          </div>

          {/* Itens */}
          {items.length > 0 && (
            <div>
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-3">
                Itens ({items.length})
              </Heading>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Descrição</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">NCM</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Qtd</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Vl. Unit.</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Total</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Categoria</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">C. Custo</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100 max-w-[200px] truncate" title={item.description ?? ''}>{item.description || '—'}</td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400 font-mono text-xs">{item.ncm || '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{item.quantity != null ? `${item.quantity} ${item.unit ?? ''}`.trim() : '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{item.unit_value != null ? formatCurrency(item.unit_value) : '—'}</td>
                        <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.total_value ?? 0)}</td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{item.category_id ? (categoryMap.get(item.category_id) ?? '—') : '—'}</td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{item.cost_center_id ? (costCenterMap.get(item.cost_center_id) ?? '—') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Arquivo */}
          {nfe.file_name && (
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Arquivo</Text>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-mono">{nfe.file_name}</Text>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Modal NFS-e ───────────────────────────────────────────────────────────────

interface NfseDetailModalProps {
  nfse: AdmNfseHeader
  items: AdmNfseItem[]
  categoryMap: Map<string, string>
  costCenterMap: Map<string, string>
  onClose: () => void
}

const NfseDetailModal: React.FC<NfseDetailModalProps> = ({ nfse, items, categoryMap, costCenterMap, onClose }) => {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
              NFS-e {nfse.number ? `nº ${nfse.number}` : '—'}
              {nfse.rps_number ? ` · RPS ${nfse.rps_number}` : ''}
            </Heading>
            <Text size="xs" color="muted" className="mt-0.5 font-mono">{nfse.access_key}</Text>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-6">

          {/* Dados gerais */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Emissão</Text>
              <p className="text-gray-900 dark:text-gray-100">{formatDate(nfse.issue_date)}</p>
            </div>
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Competência</Text>
              <p className="text-gray-900 dark:text-gray-100">
                {nfse.competence_date
                  ? new Date(nfse.competence_date + 'T00:00:00').toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })
                  : '—'}
              </p>
            </div>
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Prestador</Text>
              <p className="text-gray-900 dark:text-gray-100 font-medium">{nfse.provider_name || '—'}</p>
              {nfse.provider_cnpj && <Text size="xs" color="muted">{nfse.provider_cnpj}</Text>}
              {nfse.provider_im && <Text size="xs" color="muted">IM: {nfse.provider_im}</Text>}
            </div>
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Tomador</Text>
              <p className="text-gray-900 dark:text-gray-100">{nfse.customer_name || '—'}</p>
              {nfse.customer_document && <Text size="xs" color="muted">{nfse.customer_document}</Text>}
            </div>
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Município</Text>
              <p className="text-gray-900 dark:text-gray-100">{nfse.municipality || '—'}</p>
            </div>
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Forma de Pagamento</Text>
              <p className="text-gray-900 dark:text-gray-100">{nfse.payment_method || '—'}</p>
            </div>
          </div>

          {/* Valores */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: 'Serviços', value: nfse.total_services },
              { label: 'Desconto', value: nfse.total_discount },
              { label: 'Valor Líquido', value: nfse.net_value, highlight: true },
            ].map(({ label, value, highlight }) => (
              <div key={label} className={`rounded-lg p-3 ${highlight ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">{label}</Text>
                <p className={`text-base font-bold mt-0.5 ${highlight ? 'text-purple-600 dark:text-purple-400' : 'text-gray-900 dark:text-gray-100'}`}>
                  {formatCurrency(value ?? 0)}
                </p>
              </div>
            ))}
          </div>

          {/* Itens */}
          {items.length > 0 && (
            <div>
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-3">
                Itens ({items.length})
              </Heading>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Descrição</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Cód. Serviço</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Qtd</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Vl. Unit.</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Total</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Categoria</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">C. Custo</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100 max-w-[200px] truncate" title={item.description ?? ''}>{item.description || '—'}</td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400 font-mono text-xs">{item.service_code || '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{item.quantity != null ? item.quantity : '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{item.unit_value != null ? formatCurrency(item.unit_value) : '—'}</td>
                        <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.total_value ?? 0)}</td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{item.category_id ? (categoryMap.get(item.category_id) ?? '—') : '—'}</td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{item.cost_center_id ? (costCenterMap.get(item.cost_center_id) ?? '—') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Arquivo */}
          {nfse.file_name && (
            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wider font-medium mb-0.5">Arquivo</Text>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-mono">{nfse.file_name}</Text>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Componente Painel de Gráficos ─────────────────────────────────────────────

interface ChartPanelProps {
  title: string
  byCategory: AdmGroupRow[]
  byCostCenter: AdmGroupRow[]
  bySupplier: AdmGroupRow[]
  thirdAxisTitle?: string
  thirdAxisTooltip?: string
}

const ChartPanel: React.FC<ChartPanelProps> = ({
  title,
  byCategory,
  byCostCenter,
  bySupplier,
  thirdAxisTitle = 'Fornecedor',
  thirdAxisTooltip = 'Ranking de fornecedores/prestadores por valor total no período filtrado.',
}) => {
  const barCategoryData = byCategory.slice(0, 8).map((r) => ({ name: r.label, total: r.total }))
  const barCostData = byCostCenter.slice(0, 10).map((r) => ({ name: r.label, total: r.total }))
  const barSupplierData = bySupplier.slice(0, 10).map((r) => ({ name: r.label, total: r.total }))

  const hasCategory = barCategoryData.length > 0
  const hasCostCenter = barCostData.length > 0
  const hasSupplier = barSupplierData.length > 0

  const tooltipStyle = {
    backgroundColor: 'var(--tooltip-bg, #1f2937)',
    border: '1px solid #374151',
    borderRadius: '8px',
    color: '#f9fafb',
  }

  return (
    <div className="space-y-6">
      {/* Por Categoria */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
          <span className="inline-flex items-center gap-1.5">
            {title} — Por Categoria
            <InfoTip content="Distribuição do valor total por categoria, considerando os filtros aplicados no painel." />
          </span>
        </Heading>
        {!hasCategory ? (
          <Text size="sm" color="muted">Nenhum dado disponível.</Text>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ResponsiveContainer width="100%" height={Math.max(200, barCategoryData.length * 36)}>
              <BarChart layout="vertical" data={barCategoryData} margin={{ left: 8, right: 24 }}>
                <XAxis
                  type="number"
                  tickFormatter={(v: number) =>
                    v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`
                  }
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={110}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  tickFormatter={(v: string) => (v.length > 16 ? v.slice(0, 15) + '…' : v)}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v) => formatCurrency(Number(v))}
                />
                <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                  {barCategoryData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <RankingTable rows={byCategory} emptyLabel="Sem dados." />
          </div>
        )}
      </div>

      {/* Por Centro de Custo */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
          <span className="inline-flex items-center gap-1.5">
            {title} — Por Centro de Custo
            <InfoTip content="Distribuição do valor total por centro de custo, considerando os filtros ativos." />
          </span>
        </Heading>
        {!hasCostCenter ? (
          <Text size="sm" color="muted">Nenhum dado disponível.</Text>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ResponsiveContainer width="100%" height={Math.max(200, barCostData.length * 36)}>
              <BarChart layout="vertical" data={barCostData} margin={{ left: 8, right: 24 }}>
                <XAxis
                  type="number"
                  tickFormatter={(v: number) =>
                    v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`
                  }
                  tick={{ fontSize: 11 }}
                />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v) => formatCurrency(Number(v))}
                />
                <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                  {barCostData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <RankingTable rows={byCostCenter} emptyLabel="Sem dados." />
          </div>
        )}
      </div>

      {/* Por Fornecedor */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
          <span className="inline-flex items-center gap-1.5">
            {title} — Por {thirdAxisTitle}
            <InfoTip content={thirdAxisTooltip} />
          </span>
        </Heading>
        {!hasSupplier ? (
          <Text size="sm" color="muted">Nenhum dado disponível.</Text>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ResponsiveContainer width="100%" height={Math.max(200, barSupplierData.length * 36)}>
              <BarChart layout="vertical" data={barSupplierData} margin={{ left: 8, right: 24 }}>
                <XAxis
                  type="number"
                  tickFormatter={(v: number) =>
                    v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`
                  }
                  tick={{ fontSize: 11 }}
                />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v) => formatCurrency(Number(v))}
                />
                <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                  {barSupplierData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <RankingTable rows={bySupplier} emptyLabel="Sem dados." />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Funções de agregação ──────────────────────────────────────────────────────

function groupBy(
  entries: { label: string; value: number }[],
  grandTotal: number
): AdmGroupRow[] {
  const map = new Map<string, { total: number; count: number }>()
  for (const e of entries) {
    const cur = map.get(e.label) ?? { total: 0, count: 0 }
    map.set(e.label, { total: cur.total + e.value, count: cur.count + 1 })
  }
  return Array.from(map.entries())
    .map(([label, { total, count }]) => ({
      label,
      total,
      count,
      share: grandTotal > 0 ? (total / grandTotal) * 100 : 0,
    }))
    .sort((a, b) => b.total - a.total)
}

// ── Página principal ──────────────────────────────────────────────────────────

const AdmDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('nfe')
  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [moduleInfo, setModuleInfo] = React.useState<{ display_name: string; description: string | null } | null>(null)
  const [selectedNfe, setSelectedNfe] = React.useState<AdmNfeHeader | null>(null)
  const [selectedNfse, setSelectedNfse] = React.useState<AdmNfseHeader | null>(null)

  // Dados brutos
  const [rawData, setRawData] = React.useState<Awaited<ReturnType<typeof admDashboardService.loadAll>> | null>(null)

  // Listas para selects de filtro
  const [categoryOptions, setCategoryOptions] = React.useState<{ id: string; name: string }[]>([])
  const [costCenterOptions, setCostCenterOptions] = React.useState<{ id: string; name: string }[]>([])

  // Paginação das notas do período
  const [nfePage, setNfePage] = React.useState(1)
  const [nfsePage, setNfsePage] = React.useState(1)
  const [semNfPage, setSemNfPage] = React.useState(1)
  const NOTAS_PER_PAGE = 20

  // Ordenação das notas do período
  const [nfeSort, setNfeSort] = React.useState('issue_date')
  const [nfeSortOrder, setNfeSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [nfseSort, setNfseSort] = React.useState('issue_date')
  const [nfseSortOrder, setNfseSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [semNfSort, setSemNfSort] = React.useState('expense_date')
  const [semNfSortOrder, setSemNfSortOrder] = React.useState<'asc' | 'desc'>('desc')

  const toggleDashSort = (
    current: string, setSort: (v: string) => void,
    currentOrder: 'asc' | 'desc', setOrder: (v: 'asc' | 'desc') => void,
    setPage: (v: number) => void,
  ) => (key: string) => {
    if (current === key) {
      setOrder(currentOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSort(key)
      setOrder('asc')
    }
    setPage(1)
  }

  // Fornecedores
  const [suppliersPage, setSuppliersPage] = React.useState(1)
  const [suppliersSearch, setSuppliersSearch] = React.useState('')
  const SUPPLIERS_PER_PAGE = 25
  const [suppliersSort, setSuppliersSort] = React.useState('totalValue')
  const [suppliersSortOrder, setSuppliersSortOrder] = React.useState<'asc' | 'desc'>('desc')

  // Filtros
  const [filters, setFilters] = React.useState<AdmDashboardFilters>({
    dateFrom: '',
    dateTo: '',
    categoryId: '',
    costCenterId: '',
    supplierSearch: '',
  })

  const setFilter = <K extends keyof AdmDashboardFilters>(key: K, value: AdmDashboardFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setNfePage(1)
    setNfsePage(1)
    setSemNfPage(1)
  }

  // Carregar dados
  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const data = await admDashboardService.loadAll()
      setRawData(data)
      const catOpts: { id: string; name: string }[] = []
      data.categoryMap.forEach((name, id) => catOpts.push({ id, name }))
      catOpts.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      setCategoryOptions(catOpts)

      const ccOpts: { id: string; name: string }[] = []
      data.costCenterMap.forEach((name, id) => ccOpts.push({ id, name }))
      ccOpts.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      setCostCenterOptions(ccOpts)
    } catch (err) {
      const e = err as { message?: string }
      setPageError(e?.message ?? 'Erro ao carregar dados.')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { void loadData() }, [loadData])

  React.useEffect(() => {
    const load = async () => {
      const result = await modulesService.getByName('adm_dashboard')
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name || 'ADM • Dashboard',
          description: result.data.description || null,
        })
      }
    }
    void load()
  }, [])

  // ── Filtrar NF-e headers ────────────────────────────────────────────────────

  const filteredNfe = React.useMemo<AdmNfeHeader[]>(() => {
    if (!rawData) return []
    return rawData.nfeHeaders.filter((h) => {
      if (h.deleted_at != null) return false
      if (!withinPeriod(h.issue_date, filters.dateFrom, filters.dateTo)) return false
      if (filters.supplierSearch) {
        const q = filters.supplierSearch.toLowerCase()
        const name = (h.issuer_name ?? '').toLowerCase()
        const cnpj = (h.issuer_cnpj ?? '').replace(/\D/g, '')
        if (!name.includes(q) && !cnpj.includes(q)) return false
      }
      return true
    })
  }, [rawData, filters])

  // ── Filtrar NFS-e headers ───────────────────────────────────────────────────

  const filteredNfse = React.useMemo<AdmNfseHeader[]>(() => {
    if (!rawData) return []
    return rawData.nfseHeaders.filter((h) => {
      if (h.deleted_at != null) return false
      const dateField = h.competence_date ?? h.issue_date
      if (!withinPeriod(dateField, filters.dateFrom, filters.dateTo)) return false
      if (filters.supplierSearch) {
        const q = filters.supplierSearch.toLowerCase()
        const name = (h.provider_name ?? '').toLowerCase()
        const cnpj = (h.provider_cnpj ?? '').replace(/\D/g, '')
        if (!name.includes(q) && !cnpj.includes(q)) return false
      }
      return true
    })
  }, [rawData, filters])

  // ── IDs filtrados ───────────────────────────────────────────────────────────

  const filteredNfeIds = React.useMemo(() => new Set(filteredNfe.map((h) => h.id)), [filteredNfe])
  const filteredNfseIds = React.useMemo(() => new Set(filteredNfse.map((h) => h.id)), [filteredNfse])

  // ── Itens filtrados (com filtro de categoria/centro) ────────────────────────

  const filteredNfeItems = React.useMemo(() => {
    if (!rawData) return []
    return rawData.nfeItems.filter((item) => {
      if (!filteredNfeIds.has(item.nfe_id)) return false
      if (filters.categoryId && item.category_id !== filters.categoryId) return false
      if (filters.costCenterId && item.cost_center_id !== filters.costCenterId) return false
      return true
    })
  }, [rawData, filteredNfeIds, filters.categoryId, filters.costCenterId])

  const filteredNfseItems = React.useMemo(() => {
    if (!rawData) return []
    return rawData.nfseItems.filter((item) => {
      if (!filteredNfseIds.has(item.nfse_id)) return false
      if (filters.categoryId && item.category_id !== filters.categoryId) return false
      if (filters.costCenterId && item.cost_center_id !== filters.costCenterId) return false
      return true
    })
  }, [rawData, filteredNfseIds, filters.categoryId, filters.costCenterId])

  // ── Quando há filtro de categoria/centro, restringir headers aos que têm itens ──

  const activeNfeIds = React.useMemo(() => {
    if (!filters.categoryId && !filters.costCenterId) return filteredNfeIds
    return new Set(filteredNfeItems.map((i) => i.nfe_id))
  }, [filteredNfeItems, filteredNfeIds, filters.categoryId, filters.costCenterId])

  const activeNfseIds = React.useMemo(() => {
    if (!filters.categoryId && !filters.costCenterId) return filteredNfseIds
    return new Set(filteredNfseItems.map((i) => i.nfse_id))
  }, [filteredNfseItems, filteredNfseIds, filters.categoryId, filters.costCenterId])

  const activeNfe = React.useMemo(
    () => filteredNfe.filter((h) => activeNfeIds.has(h.id)),
    [filteredNfe, activeNfeIds]
  )

  const activeNfse = React.useMemo(
    () => filteredNfse.filter((h) => activeNfseIds.has(h.id)),
    [filteredNfse, activeNfseIds]
  )

  const activeWithoutNf = React.useMemo<AdmWithoutNfRow[]>(() => {
    if (!rawData) return []
    return rawData.withoutNfRows.filter((row) => {
      if (!withinPeriod(row.expense_date, filters.dateFrom, filters.dateTo)) return false
      if (filters.categoryId && row.category_id !== filters.categoryId) return false
      if (filters.costCenterId && row.cost_center_id !== filters.costCenterId) return false
      return true
    })
  }, [rawData, filters.dateFrom, filters.dateTo, filters.categoryId, filters.costCenterId])

  // ── Helper valor NFS-e ──────────────────────────────────────────────────────

  const getNfseValue = React.useCallback(
    (h: AdmNfseHeader) => h.net_value ?? (h.total_services ?? 0),
    []
  )

  // ── KPIs globais ────────────────────────────────────────────────────────────

  const kpis = React.useMemo(() => {
    const nfeTotal = activeNfe.reduce((s, h) => s + (h.total_value ?? 0), 0)
    const nfseTotal = activeNfse.reduce((s, h) => s + getNfseValue(h), 0)
    const semNfTotal = activeWithoutNf.reduce((s, r) => s + (r.amount_paid ?? 0), 0)
    const totalValue = nfeTotal + nfseTotal + semNfTotal
    const totalDocs = activeNfe.length + activeNfse.length + activeWithoutNf.length
    const avgTicket = totalDocs > 0 ? totalValue / totalDocs : 0

    const nfeSuppliers = new Set(activeNfe.map((h) => h.issuer_cnpj ?? h.issuer_name ?? '')).size
    const nfseSuppliers = new Set(activeNfse.map((h) => h.provider_cnpj ?? h.provider_name ?? '')).size
    const uniqueSuppliers = new Set([
      ...activeNfe.map((h) => h.issuer_cnpj ?? h.issuer_name ?? ''),
      ...activeNfse.map((h) => h.provider_cnpj ?? h.provider_name ?? ''),
    ]).size

    return { totalValue, totalDocs, avgTicket, uniqueSuppliers, nfeSuppliers, nfseSuppliers, semNfTotal }
  }, [activeNfe, activeNfse, activeWithoutNf, getNfseValue])

  // ── Relação de fornecedores consolidada ─────────────────────────────────────

  type SupplierRow = {
    cnpj: string
    name: string
    type: 'NF-e' | 'NFS-e' | 'Ambos'
    docsCount: number
    totalValue: number
  }

  const allSupplierRows = React.useMemo<SupplierRow[]>(() => {
    if (!rawData) return []
    const map = new Map<string, SupplierRow>()

    activeNfe.forEach((h) => {
      const key = h.issuer_cnpj ?? h.issuer_name ?? '—'
      const existing = map.get(key)
      if (existing) {
        existing.docsCount += 1
        existing.totalValue += h.total_value ?? 0
        if (existing.type === 'NFS-e') existing.type = 'Ambos'
      } else {
        map.set(key, {
          cnpj: h.issuer_cnpj ?? '—',
          name: h.issuer_name ?? h.issuer_cnpj ?? '—',
          type: 'NF-e',
          docsCount: 1,
          totalValue: h.total_value ?? 0,
        })
      }
    })

    activeNfse.forEach((h) => {
      const key = h.provider_cnpj ?? h.provider_name ?? '—'
      const existing = map.get(key)
      if (existing) {
        existing.docsCount += 1
        existing.totalValue += getNfseValue(h)
        if (existing.type === 'NF-e') existing.type = 'Ambos'
      } else {
        map.set(key, {
          cnpj: h.provider_cnpj ?? '—',
          name: h.provider_name ?? h.provider_cnpj ?? '—',
          type: 'NFS-e',
          docsCount: 1,
          totalValue: getNfseValue(h),
        })
      }
    })

    return Array.from(map.values()).sort((a, b) => b.totalValue - a.totalValue)
  }, [activeNfe, activeNfse, rawData, getNfseValue])

  const filteredSuppliers = React.useMemo(() => {
    if (!suppliersSearch.trim()) return allSupplierRows
    const q = suppliersSearch.toLowerCase()
    return allSupplierRows.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.cnpj.replace(/\D/g, '').includes(q.replace(/\D/g, ''))
    )
  }, [allSupplierRows, suppliersSearch])

  const sortedSuppliers = React.useMemo(() => {
    return [...filteredSuppliers].sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (suppliersSort === 'name') { av = a.name.toLowerCase(); bv = b.name.toLowerCase() }
      else if (suppliersSort === 'cnpj') { av = a.cnpj; bv = b.cnpj }
      else if (suppliersSort === 'type') { av = a.type; bv = b.type }
      else if (suppliersSort === 'docsCount') { av = a.docsCount; bv = b.docsCount }
      else if (suppliersSort === 'totalValue') { av = a.totalValue; bv = b.totalValue }
      if (av < bv) return suppliersSortOrder === 'asc' ? -1 : 1
      if (av > bv) return suppliersSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredSuppliers, suppliersSort, suppliersSortOrder])

  const suppliersTotalPages = Math.max(1, Math.ceil(sortedSuppliers.length / SUPPLIERS_PER_PAGE))
  const suppliersStart = (suppliersPage - 1) * SUPPLIERS_PER_PAGE
  const pagedSuppliers = sortedSuppliers.slice(suppliersStart, suppliersStart + SUPPLIERS_PER_PAGE)

  // ── Agrupamentos NF-e ───────────────────────────────────────────────────────

  const nfeTotalValue = React.useMemo(
    () => activeNfe.reduce((s, h) => s + (h.total_value ?? 0), 0),
    [activeNfe]
  )

  const nfeByCategory = React.useMemo(() => {
    if (!rawData) return []
    const entries = filteredNfeItems.map((item) => ({
      label: item.category_id
        ? (rawData.categoryMap.get(item.category_id) ?? 'Não informado')
        : 'Não informado',
      value: item.total_value ?? 0,
    }))
    return groupBy(entries, nfeTotalValue)
  }, [filteredNfeItems, rawData, nfeTotalValue])

  const nfeByCostCenter = React.useMemo(() => {
    if (!rawData) return []
    const entries = filteredNfeItems.map((item) => ({
      label: item.cost_center_id
        ? (rawData.costCenterMap.get(item.cost_center_id) ?? 'Não informado')
        : 'Não informado',
      value: item.total_value ?? 0,
    }))
    return groupBy(entries, nfeTotalValue)
  }, [filteredNfeItems, rawData, nfeTotalValue])

  const nfeBySupplier = React.useMemo(() => {
    const entries = activeNfe.map((h) => ({
      label: h.issuer_name ?? h.issuer_cnpj ?? 'Desconhecido',
      value: h.total_value ?? 0,
    }))
    return groupBy(entries, nfeTotalValue)
  }, [activeNfe, nfeTotalValue])

  // ── Agrupamentos NFS-e ──────────────────────────────────────────────────────

  const nfseTotalValue = React.useMemo(
    () => activeNfse.reduce((s, h) => s + getNfseValue(h), 0),
    [activeNfse, getNfseValue]
  )

  const nfseByCategory = React.useMemo(() => {
    if (!rawData) return []
    const entries = filteredNfseItems.map((item) => ({
      label: item.category_id
        ? (rawData.categoryMap.get(item.category_id) ?? 'Não informado')
        : 'Não informado',
      value: item.total_value ?? 0,
    }))
    return groupBy(entries, nfseTotalValue)
  }, [filteredNfseItems, rawData, nfseTotalValue])

  const nfseByCostCenter = React.useMemo(() => {
    if (!rawData) return []
    const entries = filteredNfseItems.map((item) => ({
      label: item.cost_center_id
        ? (rawData.costCenterMap.get(item.cost_center_id) ?? 'Não informado')
        : 'Não informado',
      value: item.total_value ?? 0,
    }))
    return groupBy(entries, nfseTotalValue)
  }, [filteredNfseItems, rawData, nfseTotalValue])

  const nfseBySupplier = React.useMemo(() => {
    const entries = activeNfse.map((h) => ({
      label: h.provider_name ?? h.provider_cnpj ?? 'Desconhecido',
      value: getNfseValue(h),
    }))
    return groupBy(entries, nfseTotalValue)
  }, [activeNfse, nfseTotalValue, getNfseValue])

  // ── Agrupamentos Planilha (sem NF) ──────────────────────────────────────────

  const semNfTotalValue = React.useMemo(
    () => activeWithoutNf.reduce((s, row) => s + (row.amount_paid ?? 0), 0),
    [activeWithoutNf]
  )

  const semNfByCategory = React.useMemo(() => {
    if (!rawData) return []
    const entries = activeWithoutNf.map((row) => ({
      label: row.category_id
        ? (rawData.categoryMap.get(row.category_id) ?? 'Não informado')
        : 'Não informado',
      value: row.amount_paid ?? 0,
    }))
    return groupBy(entries, semNfTotalValue)
  }, [activeWithoutNf, rawData, semNfTotalValue])

  const semNfByCostCenter = React.useMemo(() => {
    if (!rawData) return []
    const entries = activeWithoutNf.map((row) => ({
      label: row.cost_center_id
        ? (rawData.costCenterMap.get(row.cost_center_id) ?? 'Não informado')
        : 'Não informado',
      value: row.amount_paid ?? 0,
    }))
    return groupBy(entries, semNfTotalValue)
  }, [activeWithoutNf, rawData, semNfTotalValue])

  const semNfByPaymentType = React.useMemo(() => {
    const entries = activeWithoutNf.map((row) => ({
      label: row.payment_type?.trim() || 'Não informado',
      value: row.amount_paid ?? 0,
    }))
    return groupBy(entries, semNfTotalValue)
  }, [activeWithoutNf, semNfTotalValue])

  // ── Paginação das notas ─────────────────────────────────────────────────────

  const sortedNfePaged = React.useMemo(() => {
    return [...activeNfe].sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (nfeSort === 'number') { av = a.number ?? ''; bv = b.number ?? '' }
      else if (nfeSort === 'issuer_name') { av = (a.issuer_name ?? '').toLowerCase(); bv = (b.issuer_name ?? '').toLowerCase() }
      else if (nfeSort === 'issue_date') { av = a.issue_date ?? ''; bv = b.issue_date ?? '' }
      else if (nfeSort === 'total_value') { av = a.total_value ?? 0; bv = b.total_value ?? 0 }
      if (av < bv) return nfeSortOrder === 'asc' ? -1 : 1
      if (av > bv) return nfeSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [activeNfe, nfeSort, nfeSortOrder])

  const nfeTotalPages = Math.ceil(sortedNfePaged.length / NOTAS_PER_PAGE)
  const nfeStartIndex = (nfePage - 1) * NOTAS_PER_PAGE
  const nfeEndIndex = nfeStartIndex + NOTAS_PER_PAGE
  const nfePaged = sortedNfePaged.slice(nfeStartIndex, nfeEndIndex)

  const sortedNfsePaged = React.useMemo(() => {
    return [...activeNfse].sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (nfseSort === 'number') { av = a.number ?? ''; bv = b.number ?? '' }
      else if (nfseSort === 'provider_name') { av = (a.provider_name ?? '').toLowerCase(); bv = (b.provider_name ?? '').toLowerCase() }
      else if (nfseSort === 'issue_date') { av = a.issue_date ?? ''; bv = b.issue_date ?? '' }
      else if (nfseSort === 'net_value') { av = getNfseValue(a); bv = getNfseValue(b) }
      if (av < bv) return nfseSortOrder === 'asc' ? -1 : 1
      if (av > bv) return nfseSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [activeNfse, nfseSort, nfseSortOrder, getNfseValue])

  const nfseTotalPages = Math.ceil(sortedNfsePaged.length / NOTAS_PER_PAGE)
  const nfseStartIndex = (nfsePage - 1) * NOTAS_PER_PAGE
  const nfseEndIndex = nfseStartIndex + NOTAS_PER_PAGE
  const nfsePaged = sortedNfsePaged.slice(nfseStartIndex, nfseEndIndex)

  const sortedSemNfPaged = React.useMemo(() => {
    return [...activeWithoutNf].sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (semNfSort === 'payment_type') { av = (a.payment_type ?? '').toLowerCase(); bv = (b.payment_type ?? '').toLowerCase() }
      else if (semNfSort === 'expense_date') { av = a.expense_date ?? ''; bv = b.expense_date ?? '' }
      else if (semNfSort === 'description') { av = (a.description ?? '').toLowerCase(); bv = (b.description ?? '').toLowerCase() }
      else if (semNfSort === 'installment') { av = (a.installment ?? '').toLowerCase(); bv = (b.installment ?? '').toLowerCase() }
      else if (semNfSort === 'amount_paid') { av = a.amount_paid ?? 0; bv = b.amount_paid ?? 0 }
      else if (semNfSort === 'amount_to_pay') { av = a.amount_to_pay ?? 0; bv = b.amount_to_pay ?? 0 }
      if (av < bv) return semNfSortOrder === 'asc' ? -1 : 1
      if (av > bv) return semNfSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [activeWithoutNf, semNfSort, semNfSortOrder])

  const semNfTotalPages = Math.ceil(sortedSemNfPaged.length / NOTAS_PER_PAGE)
  const semNfStartIndex = (semNfPage - 1) * NOTAS_PER_PAGE
  const semNfEndIndex = semNfStartIndex + NOTAS_PER_PAGE
  const semNfPaged = sortedSemNfPaged.slice(semNfStartIndex, semNfEndIndex)

  // ── Helpers de estilo ───────────────────────────────────────────────────────

  const tabClass = (tab: ActiveTab) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      activeTab === tab
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  const inputClass =
    'rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName="adm_dashboard"
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">

            {/* Cabeçalho */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleInfo?.display_name || 'ADM • Dashboard'}
              </Heading>
              {moduleInfo?.description && (
                <Text size="sm" color="muted" className="mt-1">{moduleInfo.description}</Text>
              )}
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="p-6 space-y-6">

                {pageError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                    <Text size="sm">{pageError}</Text>
                  </div>
                )}

                {/* ── Filtros ── */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Data inicial</label>
                      <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => setFilter('dateFrom', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Data final</label>
                      <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => setFilter('dateTo', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Categoria</label>
                      <select
                        value={filters.categoryId}
                        onChange={(e) => setFilter('categoryId', e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Todas</option>
                        {categoryOptions.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Centro de Custo</label>
                      <select
                        value={filters.costCenterId}
                        onChange={(e) => setFilter('costCenterId', e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Todos</option>
                        {costCenterOptions.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Fornecedor</label>
                      <input
                        type="text"
                        value={filters.supplierSearch}
                        onChange={(e) => setFilter('supplierSearch', e.target.value)}
                        placeholder="Nome ou CNPJ..."
                        className={`${inputClass} w-full`}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFilters({
                          dateFrom: '',
                          dateTo: '',
                          categoryId: '',
                          costCenterId: '',
                          supplierSearch: '',
                        })
                        setNfePage(1)
                        setNfsePage(1)
                        setSemNfPage(1)
                      }}
                    >
                      Limpar filtros
                    </Button>
                  </div>
                </div>

                {/* ── KPIs ── */}
                <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr_1fr] gap-4">
                  {/* Card Total Geral com subcards */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0 h-12 w-12 rounded-lg flex items-center justify-center bg-primary-500">
                        <CurrencyDollarIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="inline-flex items-center gap-1.5">
                          <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">Total Geral</Text>
                          <InfoTip content="Soma financeira de NF-e e NFS-e conforme filtros. Para NFS-e, usa valor líquido e, se vazio, total de serviços." />
                        </div>
                        <p className="text-xl font-bold text-gray-900 dark:text-white truncate">{formatCurrency(kpis.totalValue)}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1 border-t border-gray-100 dark:border-gray-700">
                      <div className="rounded-lg bg-sky-50 dark:bg-sky-900/20 px-3 py-2 min-w-0">
                        <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">NF-e • Compras</Text>
                        <p className="text-sm font-bold text-sky-700 dark:text-sky-300 mt-0.5 break-words">{formatCurrency(nfeTotalValue)}</p>
                      </div>
                      <div className="rounded-lg bg-violet-50 dark:bg-violet-900/20 px-3 py-2 min-w-0">
                        <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">NFS-e • Serviços</Text>
                        <p className="text-sm font-bold text-violet-700 dark:text-violet-300 mt-0.5 break-words">{formatCurrency(nfseTotalValue)}</p>
                      </div>
                      <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 min-w-0">
                        <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">Planilha • Sem NF</Text>
                        <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300 mt-0.5 break-words">{formatCurrency(kpis.semNfTotal)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Card Documentos com subcards */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0 h-12 w-12 rounded-lg flex items-center justify-center bg-purple-500">
                        <ChartBarIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="inline-flex items-center gap-1.5">
                          <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">Documentos</Text>
                          <InfoTip content="Quantidade de notas fiscais consideradas no período/filtros (NF-e + NFS-e)." />
                        </div>
                        <p className="text-xl font-bold text-gray-900 dark:text-white truncate">{kpis.totalDocs}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 pt-1 border-t border-gray-100 dark:border-gray-700">
                      <div className="rounded-lg bg-sky-50 dark:bg-sky-900/20 px-3 py-2">
                        <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">NF-e • Compras</Text>
                        <p className="text-sm font-bold text-sky-700 dark:text-sky-300 mt-0.5">{activeNfe.length}</p>
                      </div>
                      <div className="rounded-lg bg-violet-50 dark:bg-violet-900/20 px-3 py-2">
                        <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">NFS-e • Serviços</Text>
                        <p className="text-sm font-bold text-violet-700 dark:text-violet-300 mt-0.5">{activeNfse.length}</p>
                      </div>
                      <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2">
                        <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">Planilha • Sem NF</Text>
                        <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300 mt-0.5">{activeWithoutNf.length}</p>
                      </div>
                    </div>
                  </div>

                  {/* Card Fornecedores com subcards */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0 h-12 w-12 rounded-lg flex items-center justify-center bg-amber-500">
                        <BuildingOfficeIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="inline-flex items-center gap-1.5">
                          <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">Fornecedores</Text>
                          <InfoTip content="Total de fornecedores/prestadores únicos nas notas filtradas." />
                        </div>
                        <p className="text-xl font-bold text-gray-900 dark:text-white truncate">{kpis.uniqueSuppliers}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 pt-1 border-t border-gray-100 dark:border-gray-700">
                      <div className="rounded-lg bg-sky-50 dark:bg-sky-900/20 px-3 py-2">
                        <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">NF-e • Compras</Text>
                        <p className="text-sm font-bold text-sky-700 dark:text-sky-300 mt-0.5">
                          {kpis.nfeSuppliers} <span className="text-xs font-normal text-gray-400 dark:text-gray-500">emitente{kpis.nfeSuppliers !== 1 ? 's' : ''}</span>
                        </p>
                      </div>
                      <div className="rounded-lg bg-violet-50 dark:bg-violet-900/20 px-3 py-2">
                        <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">NFS-e • Serviços</Text>
                        <p className="text-sm font-bold text-violet-700 dark:text-violet-300 mt-0.5">
                          {kpis.nfseSuppliers} <span className="text-xs font-normal text-gray-400 dark:text-gray-500">prestador{kpis.nfseSuppliers !== 1 ? 'es' : ''}</span>
                        </p>
                      </div>
                      <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2">
                        <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">Planilha • Sem NF</Text>
                        <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300 mt-0.5">
                          {activeWithoutNf.length} <span className="text-xs font-normal text-gray-400 dark:text-gray-500">lançamento{activeWithoutNf.length !== 1 ? 's' : ''}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Abas ── */}
                <div>
                  <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                    <nav className="-mb-px flex space-x-8">
                      <button type="button" onClick={() => { setActiveTab('nfe'); setNfePage(1) }} className={tabClass('nfe')}>
                        <span className="inline-flex items-center gap-1.5">
                          Compras NF-e
                          <InfoTip content="Visualizações analíticas de compras com NF-e: gráficos por categoria, centro de custo e fornecedor." />
                        </span>
                        <span className="ml-2 text-xs text-gray-400">({activeNfe.length})</span>
                      </button>
                      <button type="button" onClick={() => { setActiveTab('nfse'); setNfsePage(1) }} className={tabClass('nfse')}>
                        <span className="inline-flex items-center gap-1.5">
                          Serviços NFS-e
                          <InfoTip content="Visualizações analíticas de serviços com NFS-e, incluindo KPIs, gráficos e notas do período." />
                        </span>
                        <span className="ml-2 text-xs text-gray-400">({activeNfse.length})</span>
                      </button>
                      <button type="button" onClick={() => { setActiveTab('sem_nf'); setSemNfPage(1) }} className={tabClass('sem_nf')}>
                        <span className="inline-flex items-center gap-1.5">
                          Planilha (sem NF)
                          <InfoTip content="Visualizações analíticas dos lançamentos importados de planilha sem nota fiscal." />
                        </span>
                        <span className="ml-2 text-xs text-gray-400">({activeWithoutNf.length})</span>
                      </button>
                      <button type="button" onClick={() => { setActiveTab('suppliers'); setSuppliersPage(1) }} className={tabClass('suppliers')}>
                        <span className="inline-flex items-center gap-1.5">
                          Fornecedores
                          <InfoTip content="Consolidação de fornecedores/prestadores entre NF-e e NFS-e com totais e quantidade de notas." />
                        </span>
                        <span className="ml-2 text-xs text-gray-400">({allSupplierRows.length})</span>
                      </button>
                    </nav>
                  </div>

                  {activeTab === 'nfe' && (
                    activeNfe.length === 0 ? (
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-6 py-12 text-center">
                        <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                        <Text size="sm" color="muted">Nenhuma NF-e no período/filtro selecionado.</Text>
                      </div>
                    ) : (
                      <ChartPanel
                        title="NF-e"
                        byCategory={nfeByCategory}
                        byCostCenter={nfeByCostCenter}
                        bySupplier={nfeBySupplier}
                      />
                    )
                  )}

                  {activeTab === 'nfse' && (
                    activeNfse.length === 0 ? (
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-6 py-12 text-center">
                        <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                        <Text size="sm" color="muted">Nenhuma NFS-e no período/filtro selecionado.</Text>
                      </div>
                    ) : (
                      <ChartPanel
                        title="NFS-e"
                        byCategory={nfseByCategory}
                        byCostCenter={nfseByCostCenter}
                        bySupplier={nfseBySupplier}
                      />
                    )
                  )}

                  {activeTab === 'sem_nf' && (
                    activeWithoutNf.length === 0 ? (
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-6 py-12 text-center">
                        <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                        <Text size="sm" color="muted">Nenhum registro de planilha no período/filtro selecionado.</Text>
                      </div>
                    ) : (
                      <ChartPanel
                        title="Planilha (sem NF)"
                        byCategory={semNfByCategory}
                        byCostCenter={semNfByCostCenter}
                        bySupplier={semNfByPaymentType}
                        thirdAxisTitle="Tipo de Pagamento"
                        thirdAxisTooltip="Distribuição do valor pago por tipo de pagamento nos lançamentos sem NF."
                      />
                    )
                  )}

                  {activeTab === 'suppliers' && (
                    <div className="space-y-4">
                      {/* Filtro */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            value={suppliersSearch}
                            onChange={(e) => { setSuppliersSearch(e.target.value); setSuppliersPage(1) }}
                            placeholder="Buscar por nome ou CNPJ..."
                            className={`${inputClass} flex-1`}
                          />
                          <Button variant="outline" size="sm" onClick={() => { setSuppliersSearch(''); setSuppliersPage(1) }}>
                            Limpar filtros
                          </Button>
                        </div>
                      </div>

                      {/* Tabela */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                        {filteredSuppliers.length === 0 ? (
                          <div className="px-4 py-12 text-center">
                            <BuildingOfficeIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                            <Text size="sm" color="muted">
                              {allSupplierRows.length === 0
                                ? 'Nenhum fornecedor encontrado no período.'
                                : 'Nenhum fornecedor encontrado com os filtros aplicados.'}
                            </Text>
                          </div>
                        ) : (
                          <>
                            <table className="w-full min-w-[700px] divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                              <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                  <SortableTableHeader sortKey="name" currentSort={suppliersSort} sortOrder={suppliersSortOrder} onSort={toggleDashSort(suppliersSort, setSuppliersSort, suppliersSortOrder, setSuppliersSortOrder, setSuppliersPage)} className="px-4 py-3">Nome</SortableTableHeader>
                                  <SortableTableHeader sortKey="cnpj" currentSort={suppliersSort} sortOrder={suppliersSortOrder} onSort={toggleDashSort(suppliersSort, setSuppliersSort, suppliersSortOrder, setSuppliersSortOrder, setSuppliersPage)} className="px-4 py-3">CNPJ</SortableTableHeader>
                                  <SortableTableHeader sortKey="type" currentSort={suppliersSort} sortOrder={suppliersSortOrder} onSort={toggleDashSort(suppliersSort, setSuppliersSort, suppliersSortOrder, setSuppliersSortOrder, setSuppliersPage)} className="px-4 py-3 text-center">Tipo</SortableTableHeader>
                                  <SortableTableHeader sortKey="docsCount" currentSort={suppliersSort} sortOrder={suppliersSortOrder} onSort={toggleDashSort(suppliersSort, setSuppliersSort, suppliersSortOrder, setSuppliersSortOrder, setSuppliersPage)} className="px-4 py-3 text-right">Notas</SortableTableHeader>
                                  <SortableTableHeader sortKey="totalValue" currentSort={suppliersSort} sortOrder={suppliersSortOrder} onSort={toggleDashSort(suppliersSort, setSuppliersSort, suppliersSortOrder, setSuppliersSortOrder, setSuppliersPage)} className="px-4 py-3 text-right">Total</SortableTableHeader>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {pagedSuppliers.map((s, idx) => (
                                  <tr key={s.cnpj + s.name} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                    <td className="px-4 py-3 text-gray-400 dark:text-gray-500 font-mono text-xs">{suppliersStart + idx + 1}</td>
                                    <td className="px-4 py-3 text-gray-900 dark:text-white font-medium max-w-[280px] truncate" title={s.name}>{s.name}</td>
                                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{s.cnpj !== '—' ? s.cnpj : '—'}</td>
                                    <td className="px-4 py-3 text-center">
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                        s.type === 'NF-e'
                                          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                                          : s.type === 'NFS-e'
                                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                                      }`}>
                                        {s.type}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">{s.docsCount}</td>
                                    <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white whitespace-nowrap">{formatCurrency(s.totalValue)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {suppliersTotalPages > 1 && (
                              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                                <Text size="sm" color="muted">
                                  Página {suppliersPage} de {suppliersTotalPages} ({filteredSuppliers.length} {filteredSuppliers.length === 1 ? 'fornecedor' : 'fornecedores'})
                                </Text>
                                <div className="flex items-center gap-2">
                                  <Button variant="outline" size="sm" onClick={() => setSuppliersPage((p) => Math.max(1, p - 1))} disabled={suppliersPage === 1}>
                                    Anterior
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => setSuppliersPage((p) => Math.min(suppliersTotalPages, p + 1))} disabled={suppliersPage === suppliersTotalPages}>
                                    Próximo
                                  </Button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Tabela resumo de notas do período ── */}
                {activeTab === 'nfe' && activeNfe.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                        Notas do período
                      </Heading>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <SortableTableHeader sortKey="number" currentSort={nfeSort} sortOrder={nfeSortOrder} onSort={toggleDashSort(nfeSort, setNfeSort, nfeSortOrder, setNfeSortOrder, setNfePage)} className="px-4 py-3">Número</SortableTableHeader>
                            <SortableTableHeader sortKey="issuer_name" currentSort={nfeSort} sortOrder={nfeSortOrder} onSort={toggleDashSort(nfeSort, setNfeSort, nfeSortOrder, setNfeSortOrder, setNfePage)} className="px-4 py-3">Emitente</SortableTableHeader>
                            <SortableTableHeader sortKey="issue_date" currentSort={nfeSort} sortOrder={nfeSortOrder} onSort={toggleDashSort(nfeSort, setNfeSort, nfeSortOrder, setNfeSortOrder, setNfePage)} className="px-4 py-3">Emissão</SortableTableHeader>
                            <SortableTableHeader sortKey="total_value" currentSort={nfeSort} sortOrder={nfeSortOrder} onSort={toggleDashSort(nfeSort, setNfeSort, nfeSortOrder, setNfeSortOrder, setNfePage)} className="px-4 py-3 text-right">Total</SortableTableHeader>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {nfePaged.map((h) => (
                            <tr
                              key={h.id}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                              onClick={() => setSelectedNfe(h)}
                            >
                              <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{h.number || '—'}</td>
                              <td className="px-4 py-2 text-gray-500 dark:text-gray-400 max-w-[220px] truncate" title={h.issuer_name ?? ''}>{h.issuer_name || '—'}</td>
                              <td className="px-4 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(h.issue_date)}</td>
                              <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatCurrency(h.total_value ?? 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {nfeTotalPages > 1 && (
                      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <Text size="sm">
                            Mostrando {nfeStartIndex + 1} a {Math.min(nfeEndIndex, activeNfe.length)} de {activeNfe.length} registros
                          </Text>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setNfePage((p) => Math.max(1, p - 1))}
                              disabled={nfePage === 1}
                              className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Anterior
                            </button>
                            <div className="flex space-x-1">
                              {Array.from({ length: Math.min(5, nfeTotalPages) }, (_, i) => {
                                let pageNumber: number
                                if (nfeTotalPages <= 5) {
                                  pageNumber = i + 1
                                } else if (nfePage <= 3) {
                                  pageNumber = i + 1
                                } else if (nfePage >= nfeTotalPages - 2) {
                                  pageNumber = nfeTotalPages - 4 + i
                                } else {
                                  pageNumber = nfePage - 2 + i
                                }
                                return (
                                  <button
                                    key={pageNumber}
                                    onClick={() => setNfePage(pageNumber)}
                                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                                      nfePage === pageNumber
                                        ? 'bg-primary-600 text-white'
                                        : 'text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                    }`}
                                  >
                                    {pageNumber}
                                  </button>
                                )
                              })}
                            </div>
                            <button
                              onClick={() => setNfePage((p) => Math.min(nfeTotalPages, p + 1))}
                              disabled={nfePage === nfeTotalPages}
                              className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Próximo
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'nfse' && activeNfse.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                        Notas do período
                      </Heading>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <SortableTableHeader sortKey="number" currentSort={nfseSort} sortOrder={nfseSortOrder} onSort={toggleDashSort(nfseSort, setNfseSort, nfseSortOrder, setNfseSortOrder, setNfsePage)} className="px-4 py-3">Número</SortableTableHeader>
                            <SortableTableHeader sortKey="provider_name" currentSort={nfseSort} sortOrder={nfseSortOrder} onSort={toggleDashSort(nfseSort, setNfseSort, nfseSortOrder, setNfseSortOrder, setNfsePage)} className="px-4 py-3">Prestador</SortableTableHeader>
                            <SortableTableHeader sortKey="issue_date" currentSort={nfseSort} sortOrder={nfseSortOrder} onSort={toggleDashSort(nfseSort, setNfseSort, nfseSortOrder, setNfseSortOrder, setNfsePage)} className="px-4 py-3">Competência</SortableTableHeader>
                            <SortableTableHeader sortKey="net_value" currentSort={nfseSort} sortOrder={nfseSortOrder} onSort={toggleDashSort(nfseSort, setNfseSort, nfseSortOrder, setNfseSortOrder, setNfsePage)} className="px-4 py-3 text-right">Valor Líquido</SortableTableHeader>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {nfsePaged.map((h) => (
                            <tr
                              key={h.id}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                              onClick={() => setSelectedNfse(h)}
                            >
                              <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{h.number || '—'}</td>
                              <td className="px-4 py-2 text-gray-500 dark:text-gray-400 max-w-[220px] truncate" title={h.provider_name ?? ''}>{h.provider_name || '—'}</td>
                              <td className="px-4 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                {h.competence_date
                                  ? new Date(h.competence_date + 'T00:00:00').toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })
                                  : formatDate(h.issue_date)}
                              </td>
                              <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatCurrency(getNfseValue(h))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {nfseTotalPages > 1 && (
                      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <Text size="sm">
                            Mostrando {nfseStartIndex + 1} a {Math.min(nfseEndIndex, activeNfse.length)} de {activeNfse.length} registros
                          </Text>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setNfsePage((p) => Math.max(1, p - 1))}
                              disabled={nfsePage === 1}
                              className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Anterior
                            </button>
                            <div className="flex space-x-1">
                              {Array.from({ length: Math.min(5, nfseTotalPages) }, (_, i) => {
                                let pageNumber: number
                                if (nfseTotalPages <= 5) {
                                  pageNumber = i + 1
                                } else if (nfsePage <= 3) {
                                  pageNumber = i + 1
                                } else if (nfsePage >= nfseTotalPages - 2) {
                                  pageNumber = nfseTotalPages - 4 + i
                                } else {
                                  pageNumber = nfsePage - 2 + i
                                }
                                return (
                                  <button
                                    key={pageNumber}
                                    onClick={() => setNfsePage(pageNumber)}
                                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                                      nfsePage === pageNumber
                                        ? 'bg-primary-600 text-white'
                                        : 'text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                    }`}
                                  >
                                    {pageNumber}
                                  </button>
                                )
                              })}
                            </div>
                            <button
                              onClick={() => setNfsePage((p) => Math.min(nfseTotalPages, p + 1))}
                              disabled={nfsePage === nfseTotalPages}
                              className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Próximo
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'sem_nf' && activeWithoutNf.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                        Lançamentos do período
                      </Heading>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <SortableTableHeader sortKey="payment_type" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleDashSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3">Tipo pgto</SortableTableHeader>
                            <SortableTableHeader sortKey="expense_date" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleDashSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3">Data</SortableTableHeader>
                            <SortableTableHeader sortKey="description" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleDashSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3">Descrição</SortableTableHeader>
                            <SortableTableHeader sortKey="installment" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleDashSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3">Parcela</SortableTableHeader>
                            <SortableTableHeader sortKey="amount_paid" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleDashSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3 text-right">Valor pago</SortableTableHeader>
                            <SortableTableHeader sortKey="amount_to_pay" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleDashSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3 text-right">Valor a pagar</SortableTableHeader>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {semNfPaged.map((row) => (
                            <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{row.payment_type || '—'}</td>
                              <td className="px-4 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(row.expense_date)}</td>
                              <td className="px-4 py-2 text-gray-500 dark:text-gray-400 max-w-[360px] truncate" title={row.description ?? ''}>{row.description || '—'}</td>
                              <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{row.installment || '—'}</td>
                              <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatCurrency(row.amount_paid ?? 0)}</td>
                              <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatCurrency(row.amount_to_pay ?? 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {semNfTotalPages > 1 && (
                      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <Text size="sm">
                            Mostrando {semNfStartIndex + 1} a {Math.min(semNfEndIndex, activeWithoutNf.length)} de {activeWithoutNf.length} registros
                          </Text>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setSemNfPage((p) => Math.max(1, p - 1))}
                              disabled={semNfPage === 1}
                              className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Anterior
                            </button>
                            <div className="flex space-x-1">
                              {Array.from({ length: Math.min(5, semNfTotalPages) }, (_, i) => {
                                let pageNumber: number
                                if (semNfTotalPages <= 5) {
                                  pageNumber = i + 1
                                } else if (semNfPage <= 3) {
                                  pageNumber = i + 1
                                } else if (semNfPage >= semNfTotalPages - 2) {
                                  pageNumber = semNfTotalPages - 4 + i
                                } else {
                                  pageNumber = semNfPage - 2 + i
                                }
                                return (
                                  <button
                                    key={pageNumber}
                                    onClick={() => setSemNfPage(pageNumber)}
                                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                                      semNfPage === pageNumber
                                        ? 'bg-primary-600 text-white'
                                        : 'text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                    }`}
                                  >
                                    {pageNumber}
                                  </button>
                                )
                              })}
                            </div>
                            <button
                              onClick={() => setSemNfPage((p) => Math.min(semNfTotalPages, p + 1))}
                              disabled={semNfPage === semNfTotalPages}
                              className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Próximo
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            )}
          </div>
        </PermissionGuard>
      </main>

      {selectedNfe && rawData && (
        <NfeDetailModal
          nfe={selectedNfe}
          items={rawData.nfeItems.filter((i) => i.nfe_id === selectedNfe.id)}
          categoryMap={rawData.categoryMap}
          costCenterMap={rawData.costCenterMap}
          onClose={() => setSelectedNfe(null)}
        />
      )}

      {selectedNfse && rawData && (
        <NfseDetailModal
          nfse={selectedNfse}
          items={rawData.nfseItems.filter((i) => i.nfse_id === selectedNfse.id)}
          categoryMap={rawData.categoryMap}
          costCenterMap={rawData.costCenterMap}
          onClose={() => setSelectedNfse(null)}
        />
      )}
    </>
  )
}

export default AdmDashboard
